import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const LAYOUT_SOURCE = 'https://w3c.github.io/webappsec-privacy-considerations/';

/**
 * Keyboard layout detection via the legacy keyCode hack.
 *
 * Pressing an unhandled key (outside the "keydown for a text field" special
 * case) yields a keyCode that depends on the physical keyboard layout, not the
 * input language. Combined with navigator.language this distinguishes, e.g.,
 * a German QWERTZ physical layout with an en-US language preference, which
 * automation farms frequently get wrong.
 *
 * This needs a real key event, so it collects passively: it attaches a
 * non-blocking listener and reports however many keys it saw (possibly none, on
 * a page load with no typing). The reference site collects it the same way
 * (their `keyboard` signal feeds deviceInfo).
 */
export const keyboardProbe: Probe = {
  id: 'keyboard',
  label: 'Keyboard layout',
  layer: 'hardware',
  tier: 'client',
  category: 'hardware',
  description: 'Legacy keyCode layout fingerprint (needs a keypress)',
  sources: [LAYOUT_SOURCE],
  budgetMs: 2_000,
  // NOT requiresGesture. The probe attaches a passive listener and collects as
  // the visitor types or presses keys anywhere on the page; it never blocks and
  // never intercepts. Gating it on a gesture meant it was skipped on a
  // default scan, since nothing ever granted one. It now runs everywhere and
  // reports how many keys it actually saw.
  requiresGesture: false,

  async run(): Promise<ProbeResult> {
    if (typeof window === 'undefined') {
      return { signals: { supported: na<boolean>('unsupported', 'client', 'no window') } };
    }

    const observed: Record<string, number> = {};
    let done = false;

    const handler = (e: KeyboardEvent) => {
      if (e.isTrusted) {
        observed[`key_${e.code || 'unknown'}`] = e.keyCode;
        observed[`loc_${e.location || 0}`] = (observed[`loc_${e.location || 0}`] ?? 0) + 1;
      }
      // A handful of keys is enough to discriminate the common layouts.
      if (Object.keys(observed).length >= 6) {
        done = true;
        cleanup();
      }
    };

    const cleanup = () => {
      window.removeEventListener('keydown', handler, true);
      clearTimeout(timer);
    };

    // Bound the wait tightly. There is no form on this page (the /behavior
    // playground is gone), so a visitor has no reason to press a key during a
    // scan. Waiting the old 20s would add 20s to EVERY scan to collect almost
    // always nothing; 1.5s is a short grace period for a visitor who happens to
    // be typing, and the "no keypress" case is reported honestly rather than
    // waited out.
    const timer = setTimeout(() => {
      done = true;
      cleanup();
    }, 1_200);

    window.addEventListener('keydown', handler, true);

    // Poll briefly; the runner's budget is the real backstop.
    const started = Date.now();
    while (!done && Date.now() - started < 1_400) {
      await new Promise((r) => setTimeout(r, 100));
    }
    cleanup();

    const keyCount = Object.keys(observed).filter((k) => k.startsWith('key_')).length;
    if (keyCount === 0) {
      return {
        signals: {
          supported: ok(true, 'client'),
          samples: ok(0, 'client'),
          note: ok('no keypress observed; keyboard layout not measured', 'client'),
        },
      };
    }

    const findings: Finding[] = [];
    // Compare observed keyCodes against a nominal US-QWERTY expectation for the
    // keys we captured. A mismatch on a standard key implies a non-US layout.
    const mismatches = Object.entries(observed)
      .filter(([k]) => k.startsWith('key_'))
      .map(([k, v]) => ({ key: k.replace('key_', ''), keyCode: v }));

    return {
      signals: {
        supported: ok(true, 'client'),
        samples: ok(keyCount, 'client'),
        observed: ok(observed, 'client'),
        keyCodes: ok(mismatches, 'client'),
        // navigator.languages is what a fingerprint reader compares against.
        declaredLanguages: ok(navigator.languages ? Array.from(navigator.languages) : [], 'client'),
        declaredLanguage: ok(navigator.language, 'client'),
      },
      findings,
      // NO part contribution, deliberately.
      //
      // The obvious candidate was keyboardSampleCount, but that is a SAMPLING
      // artifact, not device identity: it is however many keys the visitor
      // happened to press during the probe's window. A short window makes that
      // number swing between runs (0 on a page nobody types on, 6 on one where
      // Playwright is holding keys), which destabilises the fingerprint hash
      // and breaks the rule that parts contain stable identity only. The raw
      // keyCodes stay visible in the signal list; the fingerprint ignores them.
    };
  },
};

registerProbe(keyboardProbe);
